import { ViewBooking } from "collins-js-sdk";
import { Sparkles, X } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useFeatureFlagContext } from "../../../../../hooks/useFeatureFlagContext";
import {
  detectSuboptimalUsage,
  SuboptimalUsageResult
} from "../../../../../services/ai/SuboptimalUsageDetector";
import { BookableArea } from "../../../../../types/venues";

const DISMISSAL_KEY = "proactiveSuggestionDismissed";
const DEBOUNCE_DELAY_MS = 2000;

interface ProactiveSuggestionProps {
  bookings: ViewBooking[];
  areas: BookableArea[];
  date: Date;
  onRunSpaceOptimiser: () => void;
}

export const ProactiveSuggestion = ({
  bookings,
  areas,
  date,
  onRunSpaceOptimiser
}: ProactiveSuggestionProps): React.ReactElement | null => {
  const { isFlagEnabled } = useFeatureFlagContext();
  const [dismissed, setDismissed] = useState(false);
  const [detectionResult, setDetectionResult] =
    useState<SuboptimalUsageResult | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousDate = useRef(date);

  const isFeatureEnabled = useMemo(
    () => isFlagEnabled("showAICoPilot"),
    [isFlagEnabled]
  );

  const dateKey =
    date instanceof Date
      ? date.toISOString().split("T")[0]
      : String(date).split("T")[0];

  const isDismissed = useCallback((): boolean => {
    try {
      const stored = sessionStorage.getItem(DISMISSAL_KEY);
      if (!stored) return false;
      const parsed = JSON.parse(stored) as { date: string };
      return parsed.date === dateKey;
    } catch {
      return false;
    }
  }, [dateKey]);

  useEffect(() => {
    if (previousDate.current !== date) {
      previousDate.current = date;
      setDismissed(false);
      setDetectionResult(null);
    }
  }, [date]);

  useEffect(() => {
    if (!isFeatureEnabled || !bookings.length) return;

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      const result = detectSuboptimalUsage(bookings, areas);
      setDetectionResult(result);
    }, DEBOUNCE_DELAY_MS);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [bookings, areas, isFeatureEnabled]);

  const handleDismiss = useCallback((): void => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISSAL_KEY, JSON.stringify({ date: dateKey }));
    } catch {
      // sessionStorage may be unavailable in some contexts
    }
  }, [date]);

  const handleRunOptimiser = useCallback((): void => {
    handleDismiss();
    onRunSpaceOptimiser();
  }, [handleDismiss, onRunSpaceOptimiser]);

  if (!isFeatureEnabled) return null;
  if (dismissed || isDismissed()) return null;
  if (!detectionResult?.isSuboptimal) return null;

  return (
    <div
      className="mx-3 mt-2 flex items-start gap-3 rounded-lg border border-[var(--borderColor)] bg-[var(--pageBackground)] p-3"
      role="alert"
      aria-label="Space optimisation suggestion"
    >
      <Sparkles
        className="mt-0.5 size-4 flex-shrink-0 text-[var(--primaryButtonSolid)]"
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm text-[var(--primaryTextColor)]">
          Consider running Space Optimiser &mdash; I&apos;ve detected{" "}
          <strong>{detectionResult.bookingCount}</strong> bookings that could be
          better arranged.
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            className="rounded-md bg-[var(--primaryButtonSolid)] px-3 py-1 text-xs font-medium text-primary-foreground transition-colors hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--primaryButtonSolid)] focus:ring-offset-1"
            onClick={handleRunOptimiser}
          >
            Run Space Optimiser
          </button>
          <button
            className="rounded-md border border-[var(--borderColor)] bg-[var(--panelBackground)] px-3 py-1 text-xs font-medium text-[var(--secondaryColor)] transition-colors hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-[var(--borderColor)] focus:ring-offset-1"
            onClick={handleDismiss}
          >
            Dismiss
          </button>
        </div>
      </div>
      <button
        className="flex-shrink-0 rounded-md p-0.5 text-[var(--secondaryColor)] transition-colors hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-[var(--borderColor)]"
        onClick={handleDismiss}
        aria-label="Dismiss suggestion"
      >
        <X className="size-4" aria-hidden="true" />
      </button>
    </div>
  );
};
